import React, { useState, useEffect } from "react";
import { Card, Badge, Avatar, Button } from "components/ui";
import {
  CalendarDaysIcon,
  ClockIcon,
  UserCircleIcon,
  PencilSquareIcon,
  CheckCircleIcon,
  PlusIcon,
  ArrowPathIcon,
  FlagIcon,
  FireIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { hasPermission } from "utils/permissionsUtils";
import { getCommunicationAndTelephonyStatus } from "../../../services/leadsService";
import { formatDate, formatTime, getLocalDateTime } from "../../../utils/dateUtils";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";

// Final statuses that cannot be updated further
const FINAL_STATUS_IDS = [2, 3, 4, 5]; // Completed, Missed, Cancelled, Failed
const FINAL_STATUS_NAMES = ['Completed', 'Missed', 'Cancelled', 'Failed'];

// Helper to check if a follow-up status is final
const isStatusFinal = (followUp) => {
  if (!followUp) return false;
  const statusId = followUp.StatusId || followUp.statusId;
  const statusName = followUp.StatusName || followUp.status;
  return FINAL_STATUS_IDS.includes(statusId) || FINAL_STATUS_NAMES.includes(statusName);
};

// Helper to check if a follow-up is overdue
const isFollowUpOverdue = (followUp) => {
  if (!followUp || !followUp.ScheduledDate) return false;
  const scheduledDate = getLocalDateTime(followUp.ScheduledDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return scheduledDate < today;
};

const StatusCards = ({
  isMobile,
  leadData,
  onAssignOwner,
  onEditFollowUp,
  onUpdateStatus,
  onAddFollowUp,
  // Lead Management handlers
  onChangeStage,
  onSetPriority,
  onSetInteractionLevel,
  onAssignAcademic,
  onAssignAccountant,
}) => {
  // State for communication and telephony stats
  const [communicationStats, setCommunicationStats] = useState({
    emailSent: 0,
    smsSent: 0,
  });
  const [telephonyStats, setTelephonyStats] = useState({
    inboundCalls: 0,
    outboundCalls: 0,
  });
  const [currentFollowUpIndex, setCurrentFollowUpIndex] = useState(0);

  const leadId = leadData?.id || leadData?.leadId || leadData?.LeadId;

  // Fetch communication and telephony stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!leadId) {
        return;
      }

      try {
        const response = await getCommunicationAndTelephonyStatus(leadId);
        if (response?.success && response.data) {
          const statusData = response.data;

          setCommunicationStats({
            emailSent:
              statusData.emailSent ??
              statusData.EmailSent ??
              0,
            smsSent:
              statusData.smsSent ??
              statusData.SMSSent ??
              0,
          });

          setTelephonyStats({
            inboundCalls:
              statusData.inboundCalls ??
              statusData.inboundCall ??
              statusData.InBoundCalls ??
              0,
            outboundCalls:
              statusData.outboundCalls ??
              statusData.outboundCall ??
              statusData.OutBoundCalls ??
              0,
          });
        }
      } catch (error) {
        console.error("Error fetching communication stats:", error);
      }
    };
    fetchStats();
  }, [leadId]);
  // Helper function to get the next upcoming follow-up
  const getNextFollowUp = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return null;
    }

    // Filter pending follow-ups and sort by date
    const pendingFollowUps = leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort((a, b) => getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate));

    return pendingFollowUps.length > 0 ? pendingFollowUps[0] : null;
  };

  // Helper function to get all pending follow-ups (for carousel)
  const getPendingFollowUps = () => {
    if (
      !leadData ||
      !leadData.upcomingFollowUps ||
      leadData.upcomingFollowUps.length === 0
    ) {
      return [];
    }

    return leadData.upcomingFollowUps
      .filter(
        (followUp) =>
          followUp.Status === 1 || followUp.StatusName === "Pending",
      )
      .sort((a, b) => getLocalDateTime(a.ScheduledDate) - getLocalDateTime(b.ScheduledDate));
  };

  const allPendingFollowUps = getPendingFollowUps();
  const currentFollowUp = allPendingFollowUps[currentFollowUpIndex] || null;

  // Reset index when follow-ups change
  useEffect(() => {
    if (currentFollowUpIndex >= allPendingFollowUps.length) {
      setCurrentFollowUpIndex(Math.max(0, allPendingFollowUps.length - 1));
    }
  }, [allPendingFollowUps.length, currentFollowUpIndex]);

  // Navigation handlers
  const handlePrevFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev > 0 ? prev - 1 : allPendingFollowUps.length - 1
    );
  };

  const handleNextFollowUp = () => {
    setCurrentFollowUpIndex((prev) =>
      prev < allPendingFollowUps.length - 1 ? prev + 1 : 0
    );
  };

  const isAssigned = (name) => {
    if (!name) return false;
    const n = name.toString().trim().toLowerCase();
    return n !== "n/a" && n !== "unassigned" && n !== "";
  };

  const leadOwnerName = leadData?.assignedOwner;
  const isOwnerAssigned = isAssigned(leadOwnerName);

  const academicCounsellorName = leadData?.SourceDetails?.LeadAcademicCounsellorName || leadData?.LeadAcademicCounsellorName;
  const isAcademicAssigned = isAssigned(academicCounsellorName);

  const accountsCounsellorName = leadData?.SourceDetails?.LeadAccountantCounsellorName || leadData?.accountsCounsellorName || leadData?.accountsCounsellor;
  const isAccountsAssigned = isAssigned(accountsCounsellorName);

  const hasAnyPersonnel = isOwnerAssigned || isAcademicAssigned || isAccountsAssigned;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Lead Management Section */}
      <div>
        <h3 className={`${isMobile ? "text-sm" : "text-base"} dark:text-dark-50 font-semibold text-gray-900 mb-3`}>
          Lead Management
        </h3>
        <div className={`grid ${isMobile ? "grid-cols-1 gap-2" : "grid-cols-3 gap-3"}`}>
          {hasPermission("MLLE") && onChangeStage && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onChangeStage}
              disabled={leadData?.IsStudent}
              className={`justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <ArrowPathIcon className="mr-2 h-4 w-4" />
              Change Stage
            </Button>
          )}
          {hasPermission("MLLE") && onSetPriority && (
            <Button
              variant="outlined"
              color="warning"
              onClick={onSetPriority}
              disabled={leadData?.IsStudent}
              className={`justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <FlagIcon className="mr-2 h-4 w-4" />
              Set Priority
            </Button>
          )}
          {hasPermission("MLLE") && onSetInteractionLevel && (
            <Button
              variant="outlined"
              color="neutral"
              onClick={onSetInteractionLevel}
              disabled={leadData?.IsStudent}
              className={`justify-start text-sm ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <FireIcon className="mr-2 h-4 w-4" />
              Set Interaction Level
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards Grid - 3 columns */}
      <div
        className={`grid ${isMobile ? "mb-4 grid-cols-1 gap-3" : "mb-0 grid-cols-1 gap-6 md:grid-cols-3"}`}
      >
        {/* Communication Stats Card */}
        <Card
          className={`${isMobile ? "p-3" : "p-5"} transition-shadow hover:shadow-md`}
        >
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-pink-100 dark:bg-pink-900/20">
              <PaperAirplaneIcon className="h-5 w-5 text-pink-600 dark:text-pink-400" />
            </div>
            <h3
              className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 font-semibold text-gray-900`}
            >
              Communication Stats
            </h3>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Email Sent:</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{communicationStats.emailSent}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">SMS Sent:</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{communicationStats.smsSent}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Inbound Call:</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{telephonyStats.inboundCalls}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">Outbound Call:</span>
              <span className="font-semibold text-blue-600 dark:text-blue-400">{telephonyStats.outboundCalls}</span>
            </div>
          </div>
        </Card>

        {/* Upcoming Follow-ups Card */}
        <Card
          className={`${isMobile ? "p-3" : "p-5"} transition-shadow hover:shadow-md relative flex flex-col`}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/20">
                <CalendarDaysIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3
                className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 font-semibold text-gray-900`}
              >
                Follow-ups
              </h3>
            </div>
            {/* Three Dot Menu Button */}
            {currentFollowUp && hasPermission("FUPSEFUP") && (
              <Popover className="relative">
                <PopoverButton className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors">
                  <EllipsisVerticalIcon className="h-5 w-5" />
                </PopoverButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <PopoverPanel className="absolute right-0 z-10 mt-1 w-40 rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-1.5 space-y-1">
                      {onEditFollowUp && !isStatusFinal(currentFollowUp) && (
                        <button
                          onClick={() => {
                            if (leadData?.IsStudent) return;
                            onEditFollowUp(currentFollowUp);
                            document.querySelector('[data-testid="popover-button"]')?.click();
                          }}
                          disabled={leadData?.IsStudent}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm flex items-center gap-2 transition-colors ${leadData?.IsStudent ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                          Edit
                        </button>
                      )}
                      {onUpdateStatus && !isStatusFinal(currentFollowUp) && (
                        <button
                          onClick={() => {
                            if (leadData?.IsStudent) return;
                            onUpdateStatus(currentFollowUp);
                            document.querySelector('[data-testid="popover-button"]')?.click();
                          }}
                          disabled={leadData?.IsStudent}
                          className={`w-full text-left px-3 py-2 rounded-md hover:bg-green-50 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 text-sm flex items-center gap-2 transition-colors ${leadData?.IsStudent ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                          Update Status
                        </button>
                      )}
                    </div>
                  </PopoverPanel>
                </Transition>
              </Popover>
            )}
          </div>
          <div
            className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-300 text-gray-500 flex-1 mb-3`}
          >
            {currentFollowUp ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                    <CalendarDaysIcon className="h-3 w-3" />
                    <span className="font-medium">
                      {formatDate(currentFollowUp.ScheduledDate)}
                    </span>
                    <ClockIcon className="ml-1 h-3 w-3" />
                    <span>
                      {currentFollowUp.ScheduledDate && currentFollowUp.ScheduledTime
                        ? formatTime(`${currentFollowUp.ScheduledDate}T${currentFollowUp.ScheduledTime}`)
                        : currentFollowUp.scheduledTime || ''}
                    </span>
                  </div>
                  {isFollowUpOverdue(currentFollowUp) && (
                    <Badge
                      variant="soft"
                      color="error"
                      className="text-xs whitespace-nowrap"
                    >
                      Overdue
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="dark:text-dark-400 text-xs text-gray-500">
                    {currentFollowUp.AssignedToName || currentFollowUp.AssignedTo}
                  </span>
                </div>
                {currentFollowUp.Remarks && (
                  <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                    {currentFollowUp.Remarks}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-2">
                <span className="dark:text-dark-400 text-sm text-gray-400">
                  No follow-ups
                </span>
              </div>
            )}
          </div>

          {/* Carousel Navigation */}
          {allPendingFollowUps.length > 1 && (
            <div className="flex items-center justify-between gap-2 mb-3 px-2">
              <button
                onClick={handlePrevFollowUp}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                title="Previous follow-up"
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              <div className="flex-1 flex justify-center items-center gap-2">
                <span className={`text-xs font-medium ${isFollowUpOverdue(currentFollowUp) ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                  {currentFollowUpIndex + 1} of {allPendingFollowUps.length}
                </span>
                {isFollowUpOverdue(currentFollowUp) && (
                  <div className="h-2 w-2 rounded-full bg-red-600 animate-pulse"></div>
                )}
              </div>
              <button
                onClick={handleNextFollowUp}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 transition-colors"
                title="Next follow-up"
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Create Follow-up Button */}
          {onAddFollowUp && hasPermission("FUPSCFUP") && (
            <Button
              onClick={onAddFollowUp}
              disabled={leadData?.IsStudent}
              variant="outlined"
              color="primary"
              size="sm"
              className={`w-full justify-center text-sm mt-auto ${leadData?.IsStudent ? "opacity-60 cursor-not-allowed" : ""}`}
            >
              <PlusIcon className="mr-2 h-4 w-4" />
              Create Follow-up
            </Button>
          )}
        </Card>

        {/* Assigned Personnel Card */}
        {hasAnyPersonnel && (
          <Card
            className={`${isMobile ? "p-3" : "p-5"} transition-shadow hover:shadow-md relative group flex flex-col`}
          >
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900/20">
                <UserCircleIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h3
                className={`${isMobile ? "text-xs" : "text-sm"} dark:text-dark-50 font-semibold text-gray-900`}
              >
                Assigned Personnel
              </h3>
            </div>
            <div className={`${isMobile ? "text-xs" : "text-sm"} flex-1 mb-3 space-y-4 overflow-y-auto max-h-[160px] pr-2 custom-scrollbar`}>
              {/* Lead Owner */}
              {isOwnerAssigned && (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={leadOwnerName}
                      size={isMobile ? 8 : 9}
                      initialColor="primary"
                    />
                    <div>
                      <p className={`${isMobile ? "text-sm" : "text-sm"} dark:text-dark-50 font-medium text-gray-900`}>
                        {leadOwnerName}
                      </p>
                      <p className={`${isMobile ? "text-[10px]" : "text-xs"} dark:text-dark-400 text-gray-500`}>
                        Lead Owner
                      </p>
                    </div>
                  </div>
                  {onAssignOwner && hasPermission("MLASGN") && (
                    <button
                      onClick={() => {
                        if (leadData?.IsStudent) return;
                        onAssignOwner();
                      }}
                      disabled={leadData?.IsStudent}
                      className={`p-1.5 rounded-lg transition-colors ${
                        leadData?.IsStudent
                          ? "opacity-40 cursor-not-allowed text-gray-300 dark:text-gray-600"
                          : "text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      }`}
                      title={leadData?.IsStudent ? "Cannot change owner of a student" : "Change Lead Owner"}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Academic Counsellor */}
              {isAcademicAssigned && (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={academicCounsellorName}
                      size={isMobile ? 8 : 9}
                      initialColor="success"
                    />
                    <div>
                      <p className={`${isMobile ? "text-sm" : "text-sm"} dark:text-dark-50 font-medium text-gray-900`}>
                        {academicCounsellorName}
                      </p>
                      <p className={`${isMobile ? "text-[10px]" : "text-xs"} dark:text-dark-400 text-gray-500`}>
                        Academic Counsellor
                      </p>
                    </div>
                  </div>
                  {onAssignAcademic && hasPermission("MLASGN") && (
                    <button
                      onClick={() => {
                        if (leadData?.IsStudent) return;
                        onAssignAcademic();
                      }}
                      disabled={leadData?.IsStudent}
                      className={`p-1.5 rounded-lg transition-colors ${
                        leadData?.IsStudent
                          ? "opacity-40 cursor-not-allowed text-gray-300 dark:text-gray-600"
                          : "text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      }`}
                      title={leadData?.IsStudent ? "Cannot change counsellor of a student" : "Change Academic Counsellor"}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}

              {/* Accounts Counsellor */}
              {isAccountsAssigned && (
                <div className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <Avatar
                      name={accountsCounsellorName}
                      size={isMobile ? 8 : 9}
                      initialColor="warning"
                    />
                    <div>
                      <p className={`${isMobile ? "text-sm" : "text-sm"} dark:text-dark-50 font-medium text-gray-900`}>
                        {accountsCounsellorName}
                      </p>
                      <p className={`${isMobile ? "text-[10px]" : "text-xs"} dark:text-dark-400 text-gray-500`}>
                        Accounts Counsellor
                      </p>
                    </div>
                  </div>
                  {onAssignAccountant && hasPermission("MLASGN") && (
                    <button
                      onClick={() => {
                        if (leadData?.IsStudent) return;
                        onAssignAccountant();
                      }}
                      disabled={leadData?.IsStudent}
                      className={`p-1.5 rounded-lg transition-colors ${
                        leadData?.IsStudent
                          ? "opacity-40 cursor-not-allowed text-gray-300 dark:text-gray-600"
                          : "text-gray-400 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                      }`}
                      title={leadData?.IsStudent ? "Cannot change counsellor of a student" : "Change Accountant Counsellor"}
                    >
                      <PencilSquareIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default StatusCards;
